import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AsyncPipe, DecimalPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { CountListViewType } from '@compiere-ws/models/widget-center-json';
import { WidgetDataRequest } from '@compiere-ws/models/widget-data-request';
import { WidgetCenterService } from '@compiere-ws/services/widget-center/widget-center.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { TranslateModule } from '@ngx-translate/core';
import { debounce } from 'lodash';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { AbstractWidgetComponent } from '../../abstract-widget.component';
import { levelState } from '../../widget-display';
import { WidgetStateComponent } from '../widget-state/widget-state.component';

@Component({
  selector: 'iu-count-list-widget-ui',
  templateUrl: './count-list-widget-ui.component.html',
  styleUrls: ['./count-list-widget-ui.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    AsyncPipe,
    TranslateModule,
    ButtonModule,
    DecimalPipe,
    TooltipModule,
    WidgetStateComponent,
  ],
})
export default class CountListWidgetUiComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {
  items$: Observable<{ id: string; displayedName: string }[]>;
  private items: { id: string; displayedName: string }[] = [];
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  // count / list toggle, rendered by the hosting dashboard card header when headerActionsInFrame
  @ViewChild('headerActions', { static: true }) headerActionsTpl: TemplateRef<unknown>;
  headerActionsInFrame = false;

  // @ViewChild('widgetCircularProgressBg', { read: ElementRef, static: false })
  // widgetCircularProgressBgEl: ElementRef<HTMLDivElement>;
  // @ViewChild('widgetCircularProgressCover', { read: ElementRef, static: false })
  // widgetCircularProgressCoverEl: ElementRef<HTMLDivElement>;
  @ViewChild('widgetCircularProgress', { read: ElementRef, static: false })
  widgetCircularProgressEl: ElementRef<HTMLElement>;
  numberFormat = '1.0-2';
  offset = 0;
  limit = 0;
  NB_FETCH_ROWS = 50;
  isFetching = false;
  ids = ['Value', 'Name', 'DocumentNo', 'Description'];
  result = 0;
  // UI only: value shown in the ring, counts up to result
  displayedResult = 0;
  private countUpFrame: number;
  currency = '';
  scrollSub: Subscription;
  percent = 0;
  bgColor = 'inherited';
  @Input()
  activeView: string = CountListViewType.COUNT_VIEW;
  lang;
  /** value read from the server, before coefficient and precision (kept to show it again with other settings) */
  #raw: number | null = null;
  #countSub: Subscription | undefined;
  #listSub: Subscription | undefined;
  #ringFrame: number | undefined;
  #ringDeg = 0;
  /** default view last applied: a refresh keeps the view the user switched to */
  #appliedDefaultView: string | undefined;

  constructor(
    private renderer: Renderer2,
    private widgetService: WidgetCenterService,
    private store: DataStoreService,
    uiCreator: UICreatorService,
    private connectorService: SecurityManagerService
  ) {
    super(uiCreator);
    this.getDatas = debounce(this.getDatas, 200);
  }

  ngOnInit() {
    if (this.widget) {
      if (this.widget.defaultView != null) {
        this.activeView = this.widget.defaultView;
      }
    }
    this.lang = this.connectorService.getIupicsDefaultLanguage().iso_code.replace('_', '-');
  }

  updateWidget(): void {
    // the default view applies on first load or when it changes; a refresh keeps the user's choice
    if (this.widget?.defaultView && this.widget.defaultView !== this.#appliedDefaultView) {
      this.#appliedDefaultView = this.widget.defaultView;
      this.activeView = this.widget.defaultView;
    }
    // no value column: the count cannot be shown
    if (!this.widget?.aggregateColumnId) this.activeView = CountListViewType.LIST_VIEW;
    const view = this.activeView;
    // the list needs only the source; the count also needs its value column
    if ((this.widget?.tabId || this.widget?.tableId) && (this.widget?.aggregateColumnId || view === CountListViewType.LIST_VIEW)) {
      this.isLoading = true;
      // No default view: refresh the current one, else isLoading would never be reset
      this.switchTo(view);
      this.updateAllProps();
    } else {
      this.isLoading = false;
      this.resetListData();
      this.resetCountData();
    }
  }

  updateAllProps() {
    this.updateCurrency();
    this.updateNumberFormat();
    this.updateColors();
  }

  fetchMoreData(nbInitRow = -1) {
    if (this.isFetching) {
      return;
    }
    if (this.offset === -1 && nbInitRow === -1) {
      this.isFetching = false;
      return;
    }
    this.isFetching = true;
    this.getDatas(nbInitRow);
  }

  getCount() {
    if (!this.widget?.tabId && !this.widget?.tableId) {
      return;
    }
    const request: WidgetDataRequest = {
      widget: { ...this.widget, defaultView: this.activeView },
      startRow: -1,
      endRow: -1,
    };
    // a newer request wins (the editor preview asks again while settings change)
    this.#countSub?.unsubscribe();
    this.#countSub = this.widgetService
      .getWidgetDatas(request)
      .pipe(catchError(() => of(undefined)))
      .subscribe((datas) => {
        this.isLoading = false;
        // undefined = HTTP error: the error state with a retry, not a misleading 0
        this.loadError = datas === undefined;
        this.noAccess = !!datas?.noAccess;
        if (datas && !this.noAccess) this.markUpdated();
        if (datas?.identifiers && datas?.identifiers.length > 0) {
          this.ids = datas.identifiers;
        }
        this.#raw = datas?.simpleResult != null ? Number(datas.simpleResult) : 0;
        this.#showResult();
      });
  }

  /** the value read, with the current coefficient, precision, colours */
  #showResult() {
    this.result = this.formatValue(this.#raw ?? 0);
    this.animateDisplayedResult(this.result);
    this.updateAllProps();
  }

  /** colours, unit, precision, levels changed: the value already read is shown again (no new request) */
  override applyPresentation(widget: IupicsWidget): void {
    this.setPresentation(widget);
    if (this.activeView === CountListViewType.COUNT_VIEW && this.#raw !== null && !this.loadError) {
      this.#showResult();
    } else {
      this.updateAllProps();
    }
  }

  getDatas(nbInitRow = -1) {
    if (!this.widget?.tabId && !this.widget?.tableId) {
      this.isFetching = false;
      return;
    }
    if (nbInitRow > -1) {
      this.items = [];
      this.offset = 0;
    }
    this.limit = nbInitRow > -1 ? nbInitRow : this.offset + this.NB_FETCH_ROWS;
    if (nbInitRow > -1) {
      // first page again: a page still on its way belongs to the previous settings
      this.#listSub?.unsubscribe();
    }
    this.#listSub = this.widgetService
      .getWidgetDatas({
        widget: { ...this.widget, defaultView: this.activeView },
        startRow: this.offset,
        endRow: this.limit,
      })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!res) {
          this.isFetching = false;
          this.isLoading = false;
          // first page failed: error state; a later page: the rows already shown stay
          if (!this.items.length) {
            this.loadError = true;
          }
          return;
        }
        this.loadError = false;
        this.noAccess = !!res.noAccess;
        this.markUpdated();
        this.tableName = res.tableName;
        if (res?.identifiers && res?.identifiers.length > 0) {
          this.ids = res.identifiers;
        }
        if (res?.gridData?.data) {
          this.store.replaceComplexType(res.gridData);
          if (res?.gridData?.data?.length === this.limit - this.offset) {
            this.offset = this.limit;
          } else {
            this.offset = -1;
          }

          const newItems = res?.gridData?.data.map((d) => {
            return { id: d['Data_UUID'], displayedName: this.getDisplayedName(d) };
          });
          this.items = [...this.items, ...newItems];
        }
        this.isFetching = false;
        this.isLoading = false;
        this.items$ = of(this.items);
        this.setScrollEvent();
      });
  }

  getDisplayedName(data: any) {
    let displayedName = '';
    for (const k of this.ids) {
      if (k in data && data[k] !== undefined && data[k] !== null) {
        if (displayedName !== '') {
          displayedName += ' ';
        }
        if (data[k] instanceof Object) {
          displayedName += data[k].displayValue;
        } else {
          displayedName += data[k];
        }
      }
    }
    if (!displayedName && Object.keys(data).length > 0) {
      displayedName = data[Object.keys(data)[0]];
    }
    return displayedName;
  }

  trackByFn(index, item) {
    return item.id;
  }

  updateColors() {
    const normal = this.widget?.normalLevel ?? 0;
    const alert = this.widget?.alertLevel ?? 0;
    const state = levelState(this.result, normal, alert, this.displayOpts.alertDirection);
    this.bgColor =
      state === 'neutral'
        ? 'var(--primary-bg-color)'
        : state === 'normal'
          ? this.widget.normalColor || 'var(--primary-bg-color)'
          : state === 'alert'
            ? this.widget.alertColor || 'var(--primary-bg-color)'
            : this.widget.warningColor || 'var(--primary-bg-color)';
    // ring: share of the level that matters (alert when higher is worse, normal when higher is better)
    const higherWorse = this.displayOpts.alertDirection ? this.displayOpts.alertDirection === 'higherWorse' : alert > normal;
    const reference = state === 'neutral' || normal === alert ? 0 : higherWorse ? alert : normal;
    this.percent = reference ? (this.result / reference) * 100 : 100;
    this.percent = Number(this.percent.toFixed(2));
    this.#drawRing(Math.max(0, Math.min(360, this.percent * 3.6)));
  }

  /** ring filled up to deg, animated from the current fill (one frame per screen refresh, not a 5 ms timer) */
  #drawRing(target: number) {
    if (this.#ringFrame) {
      cancelAnimationFrame(this.#ringFrame);
      this.#ringFrame = undefined;
    }
    const paint = (deg: number) => {
      const el = this.widgetCircularProgressEl?.nativeElement;
      if (el) {
        this.renderer.setStyle(
          el,
          'background',
          `conic-gradient(${this.bgColor} ${deg}deg, var(--global-bg-color-1) ${deg + 0.4}deg, var(--global-bg-color-1) 359deg, ${this.bgColor} 360deg)`
        );
      }
      this.#ringDeg = deg;
    };
    const from = this.#ringDeg;
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof requestAnimationFrame === 'undefined' || from === target) {
      paint(target);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / 600, 1);
      paint(from + (target - from) * (1 - Math.pow(1 - t, 3)));
      this.#ringFrame = t < 1 ? requestAnimationFrame(step) : undefined;
    };
    this.#ringFrame = requestAnimationFrame(step);
  }

  /** UI only: count the ring number up from the shown value to the new one (~600ms, ease out). */
  private animateDisplayedResult(target: number): void {
    this.cancelCountUp();
    const from = this.displayedResult || 0;
    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || from === target || typeof requestAnimationFrame === 'undefined') {
      this.displayedResult = target;
      return;
    }
    const integers = Number.isInteger(from) && Number.isInteger(target);
    const duration = 600;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (target - from) * eased;
      this.displayedResult = t < 1 ? (integers ? Math.round(value) : value) : target;
      this.countUpFrame = t < 1 ? requestAnimationFrame(step) : undefined;
    };
    this.countUpFrame = requestAnimationFrame(step);
  }

  private cancelCountUp(): void {
    if (this.countUpFrame) {
      cancelAnimationFrame(this.countUpFrame);
      this.countUpFrame = undefined;
    }
  }

  updateCurrency() {
    this.currency = this.widget?.measureUnit;
  }

  updateNumberFormat() {
    if (this.widget.precision != null) {
      this.numberFormat = '1.0-' + this.widget.precision;
    }
  }

  switchTo(activeView: string) {
    this.loadError = false;
    if (activeView == CountListViewType.LIST_VIEW) {
      this.resetCountData();
      // a page still on its way (or debounced) belongs to the previous settings
      this.resetListData();
      this.activeView = CountListViewType.LIST_VIEW;
      this.fetchMoreData(10);
    } else if (activeView == CountListViewType.COUNT_VIEW) {
      this.resetListData();
      this.activeView = CountListViewType.COUNT_VIEW;
      this.getCount();
    } else {
      // Unknown view: nothing is loaded, so do not keep the placeholder / block the refresh
      this.isLoading = false;
    }
  }

  resetListData() {
    (this.getDatas as any).cancel?.();
    this.#listSub?.unsubscribe();
    this.isFetching = false;
    this.items = [];
    this.offset = 0;
    this.limit = 0;
    this.items$ = null;
    this.unSetScrollEvent();
  }

  resetCountData() {
    this.#countSub?.unsubscribe();
    this.#drawRing(0);
    this.#raw = null;
    this.result = 0;
    this.cancelCountUp();
    this.displayedResult = 0;
    this.currency = '';
  }

  handleScrollProgress() {
    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();
    if (end === total && !this.isFetching) {
      this.fetchMoreData();
    }
  }

  setScrollEvent() {
    if (this.viewport && (!this.scrollSub || this.scrollSub.closed)) {
      this.scrollSub = this.viewport.elementScrolled().subscribe(() => {
        this.handleScrollProgress();
      });
    }
  }

  unSetScrollEvent() {
    if (this.scrollSub) {
      this.scrollSub.unsubscribe();
    }
  }

  ngOnDestroy() {
    (this.getDatas as any).cancel?.();
    this.unSetScrollEvent();
    this.cancelCountUp();
    this.#countSub?.unsubscribe();
    this.#listSub?.unsubscribe();
    if (this.#ringFrame) {
      cancelAnimationFrame(this.#ringFrame);
    }
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }
}
